import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Column heights for the bar variant — irregular, the way real data is. */
const BAR_HEIGHTS = [52, 74, 41, 88, 63, 96, 57, 80, 46, 70, 61, 85] as const;

interface ChartCardSkeletonProps {
    /** Which chart furniture to draw. Pick the one the card actually renders. */
    readonly variant?: 'line' | 'bar' | 'pie';
    /** Plot height in px. `h-64` (256) on workflow analytics, `h-72` (288) on polls. */
    readonly plotHeight?: number;
    /** `lg` matches the default `CardTitle`; `base` matches the poll charts' override. */
    readonly titleSize?: 'base' | 'lg';
    readonly titleWidth?: string;
    /** Side stat column, as on the poll vote-distribution card. */
    readonly showSideStats?: boolean;
    readonly className?: string;
}

/** Dashed horizontal rules matching recharts' `strokeDasharray="3 3"` grid. */
function GridLines() {
    return (
        <div className="absolute inset-0 flex flex-col justify-between" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="border-t border-dashed border-border-default" />
            ))}
        </div>
    );
}

/**
 * The plot region: y-axis ticks, gridlines, and the marks themselves.
 *
 * The line variant deliberately draws no line. A fake polyline invents a shape
 * the data may contradict, and it is the single most common way a skeleton ends
 * up lying about what is loading — the axes and grid already say "chart" on
 * their own.
 */
function PlotArea({
    variant,
    height,
}: {
    readonly variant: 'line' | 'bar' | 'pie';
    readonly height: number;
}) {
    if (variant === 'pie') {
        return (
            <div className="flex items-center justify-center" style={{ height }}>
                <div className="relative">
                    <Skeleton className="size-44 rounded-full" />
                    {/* Punches the donut hole using the card's own surface colour. */}
                    <div className="absolute inset-0 m-auto size-24 rounded-full bg-bg-surface" />
                </div>
            </div>
        );
    }

    return (
        <div className="flex gap-3" style={{ height }}>
            {/* Y-axis tick labels. */}
            <div className="flex w-8 shrink-0 flex-col justify-between py-1">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-2 w-full rounded-sm" />
                ))}
            </div>

            <div className="relative min-w-0 flex-1">
                <GridLines />
                {variant === 'bar' && (
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 px-1">
                        {BAR_HEIGHTS.map((barHeight, i) => (
                            <Skeleton
                                key={i}
                                className="min-w-0 flex-1 rounded-t-sm rounded-b-none"
                                style={{ height: `${barHeight}%` }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Loading placeholder for a chart inside a `Card`.
 *
 * Composes the real `Card` / `CardHeader` / `CardContent`, so padding, border,
 * radius and header spacing match the loaded card exactly and the swap moves
 * nothing on the page.
 */
export function ChartCardSkeleton({
    variant = 'line',
    plotHeight = 256,
    titleSize = 'lg',
    titleWidth = '180px',
    showSideStats = false,
    className,
}: ChartCardSkeletonProps) {
    return (
        <Card className={className}>
            <CardHeader>
                <SkeletonText size={titleSize} width={titleWidth} />
            </CardHeader>
            <CardContent>
                {showSideStats ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(160px,200px)]">
                        <PlotArea variant={variant} height={plotHeight} />
                        <div className="flex flex-col justify-center gap-5">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i}>
                                    <SkeletonText size="xs" width="70%" />
                                    <SkeletonText size="h1" width="45%" className="mt-1" />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <PlotArea variant={variant} height={plotHeight} />
                )}

                {variant !== 'pie' && <XAxisTicks />}
            </CardContent>
        </Card>
    );
}

/** X-axis tick labels, aligned past the y-axis gutter. */
function XAxisTicks() {
    return (
        <div className="mt-2 flex justify-between gap-2 pl-11">
            {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-2 w-8 rounded-sm" />
            ))}
        </div>
    );
}

interface ChartPlotSkeletonProps {
    readonly variant?: 'line' | 'bar' | 'pie';
    readonly plotHeight?: number;
    readonly className?: string;
}

/**
 * Just the plot region — axes, grid and marks — with no card around it.
 *
 * For charts whose `Card` and `CardHeader` are already rendered by the real
 * component and are not themselves loading, which is the common case for
 * dashboard-style cards that fetch their own series.
 */
export function ChartPlotSkeleton({
    variant = 'line',
    plotHeight = 256,
    className,
}: ChartPlotSkeletonProps) {
    return (
        <div className={cn(className)}>
            <PlotArea variant={variant} height={plotHeight} />
            {variant !== 'pie' && <XAxisTicks />}
        </div>
    );
}
